import { useRef, useState, type FormEvent } from 'react';
import { Paper, Stack, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { accountApi, type AccountProfile } from '@/api/account';
import { ActionErrorAlert } from '@/components/ActionErrorAlert';
import { SubmitButton } from '@/components/SubmitButton';
import { useActionError, type ActionError } from '@/components/useActionError';
import { useFieldErrors } from '@/components/useFieldErrors';
import { useToast } from '@/components/useToast';
import { SectionGuard } from './SectionGuard';
import { useReportDirty } from './useReportDirty';
import { FIELD_WIDTH } from '@/theme';

const NAME_MAX = 100;

const FIELDS = ['name'] as const;

export interface ProfileSectionProps {
  profile: AccountProfile;
  onSaved: (profile: AccountProfile) => void;
  /** The page owns the one unsaved-changes guard (one blocker per page): report the edited state. */
  onDirtyChange?: (dirty: boolean) => void;
}

/** `GET`/`PATCH /api/account/profile` — the member's display name; email is read-only. */
export function ProfileSection({ profile, onSaved, onDirtyChange }: ProfileSectionProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const describe = useActionError();
  const nameRef = useRef<HTMLInputElement>(null);
  const [name, setName] = useState(profile.name);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ActionError | null>(null);
  const fields = useFieldErrors(FIELDS, { name: nameRef }, saving);
  const trimmed = name.trim();
  // Edited: the name differs from the saved one. Nothing changed disables Save (TMM-B5); an empty
  // name is a change, answered on submit.
  const dirty = trimmed !== profile.name;
  useReportDirty(dirty, onDirtyChange);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (saving) return;
    if (trimmed.length === 0) {
      setError(null);
      fields.fail({ name: t('validation.name') });
      return;
    }
    setSaving(true);
    setError(null);
    fields.reset();
    try {
      const saved = await accountApi.updateName(trimmed);
      // The server's name (it may normalise what was sent) is the new baseline, and the field
      // shows it, so the form is clean after a save (TMMRSL-0072).
      const savedName = saved.name || trimmed;
      setName(savedName);
      onSaved({ ...profile, name: savedName });
      toast(t('account.profile.saved'));
    } catch (err) {
      setError(describe(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="h2" component="h2" sx={{ mb: 2 }}>
        {t('account.profile.title')}
      </Typography>
      <Stack
        component="form"
        noValidate
        spacing={2}
        onSubmit={(e) => void submit(e)}
        sx={{ maxWidth: FIELD_WIDTH.lg }}
      >
        <ActionErrorAlert error={error} />
        <TextField
          label={t('account.profile.name')}
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            fields.clear('name');
          }}
          inputRef={nameRef}
          error={fields.errors.name !== undefined}
          helperText={fields.errors.name}
          slotProps={{ htmlInput: { maxLength: NAME_MAX } }}
          required
        />
        <TextField label={t('account.profile.email')} value={profile.email} disabled />
        <Stack direction="row">
          <SubmitButton type="submit" loading={saving} disabled={!dirty}>
            {t('account.profile.save')}
          </SubmitButton>
        </Stack>
      </Stack>
      {onDirtyChange === undefined ? (
        <SectionGuard when={dirty} name={t('unsaved.names.profile')} />
      ) : null}
    </Paper>
  );
}
